import { ArrowRight, ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { PortfolioProject } from "@/hooks/usePortfolio";

interface Props { project: PortfolioProject | null; categoryName?: string; onClose: () => void }

const PortfolioDetailDialog = ({ project, categoryName, onClose }: Props) => (
  <Dialog open={!!project} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-h-[92dvh] w-[calc(100%-1rem)] max-w-6xl gap-0 overflow-hidden rounded-md border-border/80 bg-card p-0 shadow-2xl [&>button]:right-4 [&>button]:top-4 [&>button]:z-30 [&>button]:h-11 [&>button]:w-11 [&>button]:rounded-full [&>button]:border [&>button]:border-foreground/20 [&>button]:bg-background/90 [&>button]:p-0 [&>button]:text-foreground [&>button]:opacity-100 [&>button]:backdrop-blur [&>button]:transition-colors hover:[&>button]:border-primary hover:[&>button]:bg-primary hover:[&>button]:text-primary-foreground [&>button>svg]:absolute [&>button>svg]:left-1/2 [&>button>svg]:top-1/2 [&>button>svg]:h-5 [&>button>svg]:w-5 [&>button>svg]:-translate-x-1/2 [&>button>svg]:-translate-y-1/2 sm:w-[calc(100%-2rem)]">
      {project && (
        <div className="grid max-h-[92dvh] overflow-y-auto lg:grid-cols-[minmax(0,1.6fr)_minmax(22rem,1fr)] lg:overflow-hidden">
          <div className="group relative min-h-64 overflow-hidden bg-muted sm:min-h-96 lg:min-h-[640px]">
            <span className="absolute inset-y-0 left-0 z-20 w-1.5 bg-primary" />
            {project.image_url ? (
              <img
                src={project.image_url}
                alt={`${project.title} screenshot`}
                className="h-full w-full object-cover object-top transition-[transform,filter] duration-700 group-hover:scale-[1.02] group-hover:saturate-125 motion-reduce:transform-none motion-reduce:transition-none"
              />
            ) : (
              <div className="flex h-full min-h-64 items-center justify-center text-muted-foreground"><ImageIcon className="h-12 w-12" /></div>
            )}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background via-background/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
              <div className="mb-4 flex items-center gap-3">
                <span className="border border-primary px-2 py-1 text-[10px] font-bold uppercase text-primary">Case study</span>
                {project.is_featured && <span className="text-xs font-medium uppercase text-muted-foreground">Featured project</span>}
              </div>
              <DialogTitle className="max-w-3xl font-display text-4xl font-bold uppercase leading-none text-foreground sm:text-5xl lg:text-6xl">
                {project.title}
              </DialogTitle>
            </div>
          </div>

          <div className="flex min-h-0 flex-col border-t border-border bg-background lg:max-h-[92dvh] lg:border-l lg:border-t-0">
            <div className="flex-1 overflow-y-auto p-6 sm:p-10 lg:p-12">
              <div className="pr-12">
                <p className="mb-1 text-xs font-bold uppercase text-primary">Category</p>
                <p className="font-medium text-muted-foreground">{categoryName ?? "Digital project"}</p>
              </div>
              {project.summary && <DialogDescription className="mt-8 text-xl font-light leading-snug text-foreground sm:text-2xl">{project.summary}</DialogDescription>}
              {project.description && (
                <div className="mt-7 space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {project.description.split(/\n\s*\n/).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
                </div>
              )}
              {!!project.tags?.length && (
                <div className="mt-8">
                  <p className="mb-3 text-[10px] font-bold uppercase text-muted-foreground">Core stack</p>
                  <div className="flex flex-wrap gap-2 font-mono">
                    {project.tags.map((tag) => <Badge key={tag} variant="outline" className="rounded-sm border-border bg-secondary/60 px-3 py-1 font-normal text-muted-foreground">{tag}</Badge>)}
                  </div>
                </div>
              )}
            </div>
            {project.project_url && (
              <div className="border-t border-border p-6 sm:p-8">
                <Button asChild size="lg" className="h-14 w-full justify-between rounded-sm px-5 font-bold uppercase">
                  <a href={project.project_url} target="_blank" rel="noopener noreferrer">
                    Visit Project <ArrowRight className="h-5 w-5" />
                  </a>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export default PortfolioDetailDialog;
